/**
 * Poseidon dashboard — data-fetching hook.
 *
 * Every page reads through this hook so that four things are consistent:
 *   1. the `as_of` timestamp of each payload is reported to the dashboard data
 *      clock, which is what the staleness indicator displays;
 *   2. a failed request leaves the previous data visible *and* flagged, rather
 *      than blanking the screen or silently showing stale values as fresh;
 *   3. polling is paused when the tab is hidden;
 *   4. in-flight requests are aborted on unmount or when the key changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import type { Fetched } from '../api/client';
import type { DataSourceMode } from '../api/types';
import { useDataClock } from '../state/DataClock';

export interface AsyncState<T> {
  data: T | null;
  /** `as_of` of the payload currently displayed. */
  asOf: string | null;
  /** When the browser last received a payload successfully. */
  loadedAt: number | null;
  provenance: 'live' | 'replay' | 'synthetic' | null;
  warnings: string[];
  loading: boolean;
  /** True while a background poll is running after the first success. */
  refreshing: boolean;
  error: ApiError | null;
  /** True when data is displayed but the most recent attempt failed. */
  staleAfterError: boolean;
  refresh: () => void;
}

export interface UseApiDataOptions {
  /** Poll cadence in ms. Omit for a single fetch. */
  pollMs?: number;
  enabled?: boolean;
  mode: DataSourceMode;
}

export function useApiData<T>(
  key: string,
  fetcher: (signal: AbortSignal) => Promise<Fetched<T>>,
  options: UseApiDataOptions,
): AsyncState<T> {
  const { pollMs, enabled = true, mode } = options;
  const [state, setState] = useState<Omit<AsyncState<T>, 'refresh'>>({
    data: null,
    asOf: null,
    loadedAt: null,
    provenance: null,
    warnings: [],
    loading: true,
    refreshing: false,
    error: null,
    staleAfterError: false,
  });
  const [nonce, setNonce] = useState(0);

  const fetcherRef = useRef(fetcher);
  const hasDataRef = useRef(false);
  const { report } = useDataClock();

  // Keep the newest fetcher without making it an effect dependency: callers
  // pass fresh closures on every render and we must not refetch because of it.
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const load = async (isInitial: boolean): Promise<void> => {
      setState((prev) => ({
        ...prev,
        loading: isInitial && !hasDataRef.current,
        refreshing: !isInitial || hasDataRef.current,
      }));
      try {
        const result = await fetcherRef.current(controller.signal);
        if (cancelled) return;
        hasDataRef.current = true;
        report(result.asOf, result.provenance);
        setState({
          data: result.data,
          asOf: result.asOf,
          loadedAt: Date.now(),
          provenance: result.provenance,
          warnings: result.warnings,
          loading: false,
          refreshing: false,
          error: null,
          staleAfterError: false,
        });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError(0, { code: 'unexpected_error', message: 'The request failed unexpectedly.' }, 'unexpected error');
        setState((prev) => ({
          ...prev,
          loading: false,
          refreshing: false,
          error: apiError,
          // Keep showing the last good payload, but say so.
          staleAfterError: hasDataRef.current,
        }));
      }
    };

    void load(true);

    let timer: number | null = null;
    if (pollMs !== undefined && pollMs > 0) {
      timer = window.setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        void load(false);
      }, pollMs);
    }

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== null) window.clearInterval(timer);
    };
  }, [key, enabled, pollMs, nonce, report, mode]);

  return { ...state, refresh };
}
